import React, { JSX, useEffect, useMemo } from 'react';
import { Controller, FieldError, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormFooter, FormGroup } from 'common/components';
import { useTranslations } from 'contexts/LocalizationContext';
import { RestaurantRequest } from 'modules/Restaurants/models/restaurant-request.model';
import * as yup from 'yup';

import FormInput from '../../common/components/FormInput/FormInput';
import { CountryResponse } from '../Countries/model/country-response';
import { CitySelect } from '../Restaurants/components/CitySelect/CitySelect';
import { CountrySelect } from '../Restaurants/components/CountrySelect/CountrySelect';
import { MAX_RESTAURANT_NAME_LENGTH } from '../Restaurants/restaurant.consts';
import { FormModel } from '../Restaurants/RestaurantForm/RestaurantForm';
import { RestaurantStatus } from '../Restaurants/RestaurantForm/RestaurantForm.form';

type WarehouseFormProps = {
  isLoading: boolean;
  formValues?: FormModel;
  onSubmit: (request: RestaurantRequest) => void;
};

const WarehouseForm = ({ isLoading, onSubmit, formValues }: WarehouseFormProps): JSX.Element => {
  const translations = useTranslations();

  const selectOptionSchema = {
    id: yup.string(),
    translations: yup.object().shape({
      en: yup.string(),
      de: yup.string(),
    }),
  };

  const schema: yup.AnyObjectSchema = yup.object().shape({
    restaurantName: yup
      .string()
      .required(translations('validation_required'))
      .max(MAX_RESTAURANT_NAME_LENGTH, translations('validation_too_long')),
    restaurantDetail: yup.string().max(255, translations('validation_too_long')).nullable(),
    country: yup.object().nullable().shape(selectOptionSchema),
    city: yup.object().nullable().shape(selectOptionSchema),
    street: yup.string().max(255, translations('validation_too_long')).nullable(),
    streetNumber: yup.string().max(255, translations('validation_too_long')).nullable(),
    postalCode: yup.string().max(255, translations('validation_too_long')).nullable(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormModel>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (formValues) {
      reset(formValues);
    }
  }, [reset, formValues]);

  const watchCountry = useWatch({ control: control, name: 'country' });
  const isCityDisabled = useMemo(
    () => (watchCountry ? !watchCountry : !formValues?.country),
    [formValues?.country, watchCountry],
  );
  const selectedCountry = useMemo(() => watchCountry ?? formValues?.country, [formValues?.country, watchCountry]);

  const handleCountryChange = (onChange: (e: unknown) => void) => {
    return (country: CountryResponse | null) => {
      onChange(country);
      setValue('address.city', null);
    };
  };

  const onFormSubmit = handleSubmit((data) => {
    onSubmit({
      restaurantName: data.restaurantName,
      restaurantDetail: data.restaurantDetail,
      address: {
        street: data.address?.street ?? undefined,
        streetNumber: data.address?.streetNumber ?? undefined,
        postalCode: data.address?.postalCode ?? undefined,
        cityId: data.address?.city?.id ?? undefined,
      },
      status: RestaurantStatus.LIVE_INVISIBLE,
      keyAccountId: '1',
    });
  });

  return (
    <form data-testid="form" onSubmit={onFormSubmit} autoComplete="off">
      <FormInput
        id={'restaurantName'}
        label={translations('warehouses_form_name')}
        validationError={errors.restaurantName?.message}
        useFormRegisterReturn={register('restaurantName')}
        setValue={setValue}
      />
      <FormInput
        id={'restaurantDetail'}
        label={translations('warehouses_form_detail')}
        validationError={errors.restaurantDetail?.message}
        useFormRegisterReturn={register('restaurantDetail')}
        setValue={setValue}
      />
      <FormInput
        id={'street'}
        label={translations('restaurants_form_address_street')}
        validationError={errors.address?.street?.message}
        useFormRegisterReturn={register('address.street')}
        setValue={setValue}
      />
      <FormInput
        id={'streetNumber'}
        label={translations('restaurants_form_address_street_number')}
        validationError={errors.address?.streetNumber?.message}
        useFormRegisterReturn={register('address.streetNumber')}
        setValue={setValue}
      />
      <FormInput
        id={'postalCode'}
        label={translations('restaurants_form_address_postal_code')}
        validationError={errors.address?.postalCode?.message}
        useFormRegisterReturn={register('address.postalCode')}
        setValue={setValue}
      />
      <FormGroup label={translations('restaurants_form_address_country')} inputId={'country'}>
        <Controller
          control={control}
          name="country"
          render={({ field: { value, name, onChange } }) => (
            <CountrySelect name={name} selectedValue={value} onSelect={handleCountryChange(onChange)} />
          )}
        ></Controller>
      </FormGroup>
      <FormGroup
        label={translations('restaurants_form_address_city')}
        inputId={'address.city'}
        validationError={(errors.address?.city as FieldError)?.message}
        labelTooltipText={translations('restaurants_form_address_city_label_tooltip')}
      >
        <Controller
          control={control}
          name="address.city"
          render={({ field: { value, name, onChange } }) => (
            <CitySelect
              name={name}
              selectedValue={value}
              onSelect={onChange}
              country={selectedCountry}
              isDisabled={isCityDisabled}
            />
          )}
        ></Controller>
      </FormGroup>
      <FormFooter isLoadingSubmitResult={isLoading} />
    </form>
  );
};

export default WarehouseForm;
